import MojitoServices from 'mojito/services';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import EventCardsCarouselUtils from 'modules/event-cards-carousel/utils.js';
import { useSortedItems } from 'modules/common/hooks';
import { pickBy, without } from 'mojito/utils';

const { convertToCard } = EventCardsCarouselUtils;
const { makeSelectEventsState, makeSelectMarketsState } =
    MojitoServices.SportsContent.Events.selectors;
const { types: ServicesTypes } = MojitoServices.Common;
const { UNAVAILABLE } = ServicesTypes.CONTENT_STATE;

const getSportId = obj => obj.sportId;

const useCards = (promotions, priorityType) => {
    const promotionCards = useMemo(
        () => (promotions ? promotions.map(convertToCard) : []),
        [promotions]
    );

    return useSortedItems(promotionCards, priorityType, getSportId);
};

/**
 * Contains custom hooks for event card carousel functionality.
 *
 * @class EventCardsCarouselHooks
 * @name hooks
 * @memberof Mojito.Modules.EventCardsCarousel
 */

/**
 * Converts promotions to cards, sort by display order and filter out cards which have events with an UNAVAILABLE state.
 * NOTE: Not to be used to convert promotions to pre built bet cards, instead use
 * {@link Mojito.Modules.EventCardsCarousel.hooks.usePreBuiltBetCardsPromotions|usePreBuiltBetCardsPromotions}.
 *
 * @function useCardsPromotions
 *
 * @param {Array<Mojito.Services.Promotions.types.Promotion>} promotions - Promotions list.
 * @param {Mojito.Services.UserSettings.types.PRIORITY_TYPES} priorityType - Priority to be used for sorting.
 *
 * @returns {Array<object>} List of cards.
 * @memberof Mojito.Modules.EventCardsCarousel.hooks
 */
export const useCardsPromotions = (promotions, priorityType) => {
    const cards = useCards(promotions, priorityType);
    const eventIds = useMemo(
        () => cards.map(card => card.content.eventId).filter(Boolean),
        [cards]
    );

    const selectEventsState = useMemo(makeSelectEventsState, []);
    const eventsState = useSelector(state => selectEventsState(eventIds, state));

    // Sometimes promotions can still have event card for outdated event. It will happen if event is over but CmsJob hasn't rerun yet.
    // Here we will detect such event cards to ignore them while rendering.
    return useMemo(() => {
        const unavailableEventIds = Object.keys(
            pickBy(eventsState, eventState => eventState === UNAVAILABLE)
        );
        const unavailableCards = cards.filter(card =>
            unavailableEventIds.includes(card.content.eventId)
        );
        return unavailableCards.length ? without(cards, ...unavailableCards) : cards;
    }, [eventsState, cards]);
};

/**
 * Converts promotions to pre built bet cards, sort by display order and filter
 * out cards which have event or market with an UNAVAILABLE state.
 * This is needed instead of {@link Mojito.Modules.EventCardsCarousel.hooks.useCardsPromotions|useCardsPromotions}
 * because pre built bet cards don't support being shown in suspended state.
 *
 * @function usePreBuiltBetCardsPromotions
 *
 * @param {Array<Mojito.Services.Promotions.types.Promotion>} promotions - Promotions list.
 * @param {Mojito.Services.UserSettings.types.PRIORITY_TYPES} priorityType - Priority to be used for sorting.
 *
 * @returns {Array<object>} List of cards.
 * @memberof Mojito.Modules.EventCardsCarousel.hooks
 */
export const usePreBuiltBetCardsPromotions = (promotions, priorityType) => {
    const cards = useCards(promotions, priorityType);
    const eventIds = useMemo(() => cards.map(card => card.content.eventId), [cards]);
    const marketIds = useMemo(() => cards.map(card => card.content.marketId), [cards]);
    const selectEventsState = useMemo(makeSelectEventsState, []);
    const eventsState = useSelector(state => selectEventsState(eventIds, state));

    const selectMarketsState = useMemo(makeSelectMarketsState, []);
    const marketsState = useSelector(state => selectMarketsState(marketIds, state));

    // PreBuiltBetBuilderPromotions can have outdated event or market.
    // It will happen if event or market is over but CmsJob hasn't rerun yet.
    // Here we will detect such promotions to ignore them while rendering.
    return useMemo(() => {
        const unavailableEventIds = Object.keys(
            pickBy(eventsState, eventState => eventState === UNAVAILABLE)
        );
        const unavailableMarketIds = Object.keys(
            pickBy(marketsState, marketState => marketState === UNAVAILABLE)
        );

        const unavailableCards = cards.filter(
            card =>
                unavailableEventIds.includes(card.content.eventId) ||
                unavailableMarketIds.includes(card.content.marketId)
        );
        return unavailableCards.length ? without(cards, ...unavailableCards) : cards;
    }, [cards, eventsState, marketsState]);
};
